import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewCard = ({ review, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    className="bg-white p-8 rounded-2xl border border-brand-border"
    data-testid={`review-card-${index}`}
  >
    {/* Stars */}
    <div className="flex gap-1 mb-4">
      {[...Array(review.rating)].map((_, i) => (
        <Star key={i} className="w-5 h-5 fill-brand-sage text-brand-sage" />
      ))}
    </div>
    
    {/* Quote */}
    <Quote className="w-8 h-8 text-brand-sage/20 mb-4" />
    
    {/* Review Text */}
    <p className="text-brand-charcoal leading-relaxed mb-6">
      "{review.review_text}"
    </p>
    
    {/* Guest Info */}
    <div className="flex items-center gap-4 pt-4 border-t border-brand-border">
      <div className="w-12 h-12 rounded-full bg-brand-sage/10 flex items-center justify-center">
        <span className="font-heading text-lg text-brand-sage">
          {review.guest_name.charAt(0)}
        </span>
      </div>
      <div>
        <p className="font-medium text-brand-charcoal">{review.guest_name}</p>
        <p className="text-sm text-muted-foreground">{review.location}</p>
        <p className="text-sm text-muted-foreground">{review.date}</p>
      </div>
    </div>
  </motion.div>
);

const HeroSection = () => (
  <section className="pt-12 pb-8 bg-brand-cream" data-testid="reviews-hero">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <p className="section-subheading">Guest Experiences</p>
        <h1 className="section-heading">Reviews</h1>
        <p className="text-muted-foreground text-lg mt-4 max-w-2xl mx-auto">
          Don't just take our word for it. Here's what our guests have to say 
          about their stay at Weaver's Nest Cottage.
        </p>
        
        {/* Rating Summary */}
        <div className="flex items-center justify-center gap-4 mt-8">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-6 h-6 fill-brand-sage text-brand-sage" />
            ))}
          </div>
          <span className="text-2xl font-heading">5.0</span>
          <span className="text-muted-foreground">Guest Favourite</span>
        </div>
      </motion.div>
    </div>
  </section>
);

const ReviewsGrid = ({ reviews }) => (
  <section className="py-12 md:py-20 bg-white" data-testid="reviews-grid">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {reviews.map((review, index) => (
          <ReviewCard key={review.id} review={review} index={index} />
        ))}
      </div>
    </div>
  </section>
);

const HighlightedReview = ({ review }) => (
  <section className="py-20 md:py-32 bg-brand-charcoal text-white" data-testid="highlighted-review">
    <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="flex justify-center gap-1 mb-8">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} className="w-8 h-8 fill-brand-sage text-brand-sage" />
          ))}
        </div>
        
        <blockquote className="font-accent text-2xl md:text-3xl lg:text-4xl italic leading-relaxed mb-10">
          "{review.review_text}"
        </blockquote>
        
        <div className="flex items-center justify-center gap-4">
          <div className="w-14 h-14 rounded-full bg-brand-sage/20 flex items-center justify-center">
            <span className="font-heading text-xl text-brand-sage">
              {review.guest_name.charAt(0)}
            </span>
          </div>
          <div className="text-left">
            <p className="font-medium">{review.guest_name}</p>
            <p className="text-sm text-gray-400">{review.location} · {review.date}</p>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reviews`);
      setReviews(res.data.reviews);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const featuredReview = reviews.find(r => r.is_featured) || reviews[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="reviews-page">
      <HeroSection />
      {featuredReview && <HighlightedReview review={featuredReview} />}
      <ReviewsGrid reviews={reviews} />
    </div>
  );
}
